import { isRight } from 'fp-ts/Either';
import React, {
  createContext,
  useEffect,
  useRef,
  useState,
  type SetStateAction,
} from 'react';
import { toast } from 'sonner';
import { createRequiredContextHook } from '../hooks/createRequiredContextHook';
import { useAsyncData } from '../hooks/useAsyncData';
import { AuthStateType, useAuth } from '../hooks/useAuth';
import { useOptimisticMutation } from '../hooks/useOptimisticMutation';
import { useTRPC } from '../hooks/useTRPC';
import { getErrorMessage } from '../utils/errorHandling';
import { captureAndReportError } from '../utils/errorReporting';

interface AlertItem {
  id: string;
  type: string;
  title: string;
  message: string;
  created: Date;
  readAt: Date | null;
  actionUrl?: string | null;
}

interface AlertContextValue {
  alerts: AlertItem[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  totalAlerts: number;
  refetchAlerts: () => Promise<void>;
  markAsRead: (alertId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  lastFetchTime: Date | null;
}

const AlertContext = createContext<AlertContextValue | undefined>(undefined);
const ALERT_ROUTE_CHANGE_EVENT = 'alert-context:route-change';

function isAlertsRoute(pathname: string) {
  return pathname.includes('/alerts');
}

interface AlertProviderProps {
  children: React.ReactNode;
  pollingInterval?: number;
  pollingIntervalWhenActive?: number;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({
  children,
  pollingInterval = 60000,
  pollingIntervalWhenActive = 30000,
}) => {
  const initialIsAlertsPageActive =
    typeof window !== 'undefined'
      ? isAlertsRoute(window.location.pathname)
      : false;
  const trpc = useTRPC();
  const [authState] = useAuth();
  const {
    data: alerts,
    setData: setAlerts,
    isLoading,
    setIsLoading,
    error,
    setError,
    invalidate,
    run,
  } = useAsyncData<AlertItem[]>({
    initialData: [],
    initialLoading: true,
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);
  const canAccessAlerts =
    authState.type === AuthStateType.Authenticated &&
    authState.user.onboardingState !== 'EmailVerification';

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPageVisibleRef = useRef(true);
  const isAlertsPageActiveRef = useRef(initialIsAlertsPageActive);
  const errorCountRef = useRef(0);
  const lastKnownUnreadCountRef = useRef(0);
  const alertsRef = useRef<AlertItem[]>(alerts);
  const fetchAlertsRef = useRef<
    ((showLoadingState?: boolean) => Promise<void>) | null
  >(null);
  const runOptimisticMutation = useOptimisticMutation();
  const [isAlertsPageActive, setIsAlertsPageActive] = useState(
    initialIsAlertsPageActive,
  );

  const updateAlerts = (updater: SetStateAction<AlertItem[]>) => {
    setAlerts((previousAlerts) => {
      const nextAlerts =
        typeof updater === 'function'
          ? (updater as (prevState: AlertItem[]) => AlertItem[])(previousAlerts)
          : updater;

      alertsRef.current = nextAlerts;
      return nextAlerts;
    });
  };

  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);

  useEffect(() => {
    isAlertsPageActiveRef.current = isAlertsPageActive;
  }, [isAlertsPageActive]);

  useEffect(() => {
    const syncAlertsPageState = () => {
      const nextIsAlertsPageActive = isAlertsRoute(window.location.pathname);
      isAlertsPageActiveRef.current = nextIsAlertsPageActive;
      setIsAlertsPageActive(nextIsAlertsPageActive);
    };

    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(
      window.history,
    );

    const dispatchRouteChangeEvent = () => {
      window.dispatchEvent(new Event(ALERT_ROUTE_CHANGE_EVENT));
    };

    window.history.pushState = function (
      ...args: Parameters<History['pushState']>
    ) {
      const result = originalPushState.apply(this, args);
      dispatchRouteChangeEvent();
      return result;
    };

    window.history.replaceState = function (
      ...args: Parameters<History['replaceState']>
    ) {
      const result = originalReplaceState.apply(this, args);
      dispatchRouteChangeEvent();
      return result;
    };

    syncAlertsPageState();
    window.addEventListener('popstate', syncAlertsPageState);
    window.addEventListener(ALERT_ROUTE_CHANGE_EVENT, syncAlertsPageState);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', syncAlertsPageState);
      window.removeEventListener(ALERT_ROUTE_CHANGE_EVENT, syncAlertsPageState);
    };
  }, []);

  const fetchAlerts = async (showLoadingState = false) => {
    if (!trpc || !trpc.alert) {
      return;
    }

    if (!canAccessAlerts) {
      return;
    }

    await run(
      () =>
        Promise.all([
          trpc.alert.listAlerts.query({
            limit: 50,
            offset: 0,
            unreadOnly: false,
          }),
          trpc.alert.getUnreadCount.query({}),
        ]),
      {
        showLoadingState,
        clearError: false,
        onSuccess: ([alertsResult, unreadResult], helpers) => {
          if (isRight(alertsResult)) {
            const newAlerts = alertsResult.right.alerts.map(
              (alert: {
                id: string;
                type: string;
                title: string;
                message: string;
                created: Date | string;
                readAt: Date | string | null;
                actionUrl?: string | null;
              }) => ({
                id: alert.id,
                type: alert.type,
                title: alert.title,
                message: alert.message,
                created: new Date(alert.created),
                readAt: alert.readAt ? new Date(alert.readAt) : null,
                actionUrl: alert.actionUrl,
              }),
            );

            updateAlerts((prevAlerts) => {
              if (prevAlerts.length > 0) {
                const previousAlertIds = new Set(prevAlerts.map((a) => a.id));
                const newAlertItems = newAlerts.filter(
                  (alert: AlertItem) => !previousAlertIds.has(alert.id),
                );

                if (
                  newAlertItems.length > 0 &&
                  !isAlertsPageActiveRef.current
                ) {
                  const latestAlert = newAlertItems[0];
                  const actionUrl = latestAlert.actionUrl;
                  toast.info(latestAlert.title, {
                    description:
                      latestAlert.message.length > 100
                        ? `${latestAlert.message.substring(0, 97)}...`
                        : latestAlert.message,
                    action: actionUrl
                      ? {
                          label: 'View',
                          onClick: () => {
                            window.location.href = actionUrl;
                          },
                        }
                      : undefined,
                  });
                }
              }
              return newAlerts;
            });

            setTotalAlerts(alertsResult.right.total);
            helpers.setError(null);
            errorCountRef.current = 0;
          }

          if (isRight(unreadResult) && 'count' in unreadResult.right) {
            const newUnreadCount = unreadResult.right.count;
            lastKnownUnreadCountRef.current = newUnreadCount;
            setUnreadCount(newUnreadCount);
          }

          setLastFetchTime(new Date());
        },
        onError: (err, helpers) => {
          errorCountRef.current++;
          const errorMessage = getErrorMessage(err, 'Failed to fetch alerts');

          captureAndReportError(err, {
            operation: 'Error fetching alerts',
            component: 'AlertContext',
            attempt: errorCountRef.current,
          });

          if (errorCountRef.current >= 3) {
            helpers.setError(errorMessage);
          }
        },
      },
    );
  };

  useEffect(() => {
    fetchAlertsRef.current = fetchAlerts;
  }, [fetchAlerts]);

  const markAsRead = async (alertId: string) => {
    if (!trpc || !trpc.alert) return;
    if (!canAccessAlerts) return;

    try {
      await runOptimisticMutation({
        captureSnapshot: () => {
          const currentAlert = alertsRef.current.find(
            (alert) => alert.id === alertId,
          );

          return {
            previousReadAt: currentAlert?.readAt ?? null,
            shouldRestoreUnreadCount: currentAlert?.readAt == null,
          };
        },
        applyOptimistic: () => {
          updateAlerts((prev) =>
            prev.map((alert) =>
              alert.id === alertId ? { ...alert, readAt: new Date() } : alert,
            ),
          );
          setUnreadCount((prev) => Math.max(0, prev - 1));
        },
        mutate: () => trpc.alert.markAsRead.mutate({ alertId }),
        validateResult: (result) => {
          if (!isRight(result) || !result.right.success) {
            throw new Error('Failed to mark as read');
          }
        },
        onSuccess: async () => {
          if (fetchAlertsRef.current) {
            await fetchAlertsRef.current();
          }
        },
        rollback: (snapshot) => {
          const currentAlert = alertsRef.current.find(
            (alert) => alert.id === alertId,
          );
          const shouldRestoreUnreadCount =
            snapshot.shouldRestoreUnreadCount && currentAlert?.readAt != null;

          updateAlerts((prev) =>
            prev.map((alert) =>
              alert.id === alertId
                ? {
                    ...alert,
                    readAt: snapshot.previousReadAt,
                  }
                : alert,
            ),
          );
          if (shouldRestoreUnreadCount) {
            setUnreadCount((prev) => prev + 1);
          }
        },
        onError: (err) => {
          toast.error('Failed to mark alert as read');
          captureAndReportError(
            '[AlertContext] Failed to mark alert as read:',
            err,
          );
        },
      });
    } catch {
      // Failure reporting and rollback are handled in the optimistic mutation.
    }
  };

  const markAllAsRead = async () => {
    if (!trpc || !trpc.alert) return;
    if (!canAccessAlerts) return;

    let optimisticReadAt: Date | null = null;

    try {
      await runOptimisticMutation({
        captureSnapshot: () => ({
          optimisticReadAtMs: (optimisticReadAt = new Date()).getTime(),
          unreadAlertIds: alertsRef.current
            .filter((alert) => alert.readAt == null)
            .map((alert) => alert.id),
        }),
        applyOptimistic: () => {
          const readAt = optimisticReadAt ?? new Date();
          updateAlerts((prev) =>
            prev.map((alert) => ({
              ...alert,
              readAt: alert.readAt || readAt,
            })),
          );
          setUnreadCount(0);
        },
        mutate: () => trpc.alert.markAllAsRead.mutate({}),
        validateResult: (result) => {
          if (!isRight(result)) {
            throw new Error('Failed to mark all as read');
          }
        },
        onSuccess: async () => {
          if (fetchAlertsRef.current) {
            await fetchAlertsRef.current();
          }
        },
        rollback: (snapshot) => {
          const unreadAlertIds = new Set(snapshot.unreadAlertIds);
          const optimisticAlertIds = new Set(
            alertsRef.current
              .filter(
                (alert) =>
                  unreadAlertIds.has(alert.id) &&
                  alert.readAt?.getTime() === snapshot.optimisticReadAtMs,
              )
              .map((alert) => alert.id),
          );

          if (optimisticAlertIds.size === 0) {
            return;
          }

          updateAlerts((prev) =>
            prev.map((alert) =>
              optimisticAlertIds.has(alert.id)
                ? {
                    ...alert,
                    readAt: null,
                  }
                : alert,
            ),
          );
          setUnreadCount((prev) => prev + optimisticAlertIds.size);
        },
        onError: (err) => {
          toast.error('Failed to mark all alerts as read');
          captureAndReportError(
            '[AlertContext] Failed to mark all alerts as read:',
            err,
          );
        },
      });
    } catch {
      // Failure reporting and rollback are handled in the optimistic mutation.
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = !document.hidden;

      if (!document.hidden && lastFetchTime && fetchAlertsRef.current) {
        const timeSinceLastFetch = Date.now() - lastFetchTime.getTime();
        const currentInterval = isAlertsPageActiveRef.current
          ? pollingIntervalWhenActive
          : pollingInterval;

        if (timeSinceLastFetch > currentInterval) {
          fetchAlertsRef.current();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [lastFetchTime, pollingInterval, pollingIntervalWhenActive]);

  useEffect(() => {
    if (!canAccessAlerts) {
      invalidate();
      updateAlerts([]);
      setUnreadCount(0);
      setTotalAlerts(0);
      setError(null);
      setIsLoading(false);
      lastKnownUnreadCountRef.current = 0;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    if (fetchAlertsRef.current) {
      // First load shows the skeleton; polling refetches stay silent.
      void fetchAlertsRef.current(true);
    }
  }, [canAccessAlerts, invalidate, setError, setIsLoading]);

  useEffect(() => {
    if (!canAccessAlerts) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const currentInterval = isAlertsPageActive
      ? pollingIntervalWhenActive
      : pollingInterval;

    pollingIntervalRef.current = setInterval(() => {
      if (isPageVisibleRef.current && fetchAlertsRef.current) {
        void fetchAlertsRef.current();
      }
    }, currentInterval);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    canAccessAlerts,
    isAlertsPageActive,
    pollingInterval,
    pollingIntervalWhenActive,
  ]);

  const value: AlertContextValue = {
    alerts,
    unreadCount,
    isLoading,
    error,
    totalAlerts,
    refetchAlerts: async () => {
      if (fetchAlertsRef.current) {
        await fetchAlertsRef.current();
      }
    },
    markAsRead,
    markAllAsRead,
    lastFetchTime,
  };

  return (
    <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
  );
};

export const useAlertContext = createRequiredContextHook(
  AlertContext,
  'AlertProvider',
  'useAlertContext',
);

export const useUnreadCount = () => {
  const { unreadCount } = useAlertContext();
  return unreadCount;
};

export const useAlerts = () => {
  const { alerts, totalAlerts, isLoading, error, refetchAlerts } =
    useAlertContext();
  return { alerts, totalAlerts, isLoading, error, refetchAlerts };
};

export const useAlertActions = () => {
  const { markAsRead, markAllAsRead } = useAlertContext();
  return { markAsRead, markAllAsRead };
};
